import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field, FieldArray } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import { OptionalItemsTable } from 'components/Subscriptions';
import useStyle from './OptionalItems.styled';
import { Button, Box } from '@material-ui/core';
import Loading from 'components/Loading';

const OptionalItems = ({ id, open, handleClick }) => {
  const classes = useStyle();

  const itemsValidationSchema = Yup.object().shape({
    id: Yup.string(),
    quantity: Yup.number().min(0, i18n.t('common.min_0')).max(1, i18n.t('common.max_1')),
  });

  const formSchema = Yup.object().shape({
    items: Yup.array().of(itemsValidationSchema),
  });

  const formRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    items: [],
  });
  const [pricing, setPricing] = useState([]);
  const dispatch = useDispatch();
  const {
    subscription,
    subscriptionPricing,
    fetchedUpdateSubscription,
    fetchingUpdateSubscription,
    fetchingSubscriptionPricing,
  } = useSelector((state) => state.subscriptions);

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    dispatch(
      SubscriptionsActions.fetchSubscriptionPricing(id, {
        rules_category: 'OPTIONAL',
      })
    );
  }, []); // eslint-disable-line

  useEffect(() => {
    if (subscription) {
      setPricing(subscription?.pricing || []);
    }
  }, [ subscription ]); // eslint-disable-line

  useEffect(() => {
    if (fetchedUpdateSubscription) {
      handleClick();
    }
  }, [ fetchedUpdateSubscription ]); // eslint-disable-line

  useEffect(() => {
    if (subscriptionPricing) {
      setInitialValues({
        items:
          subscriptionPricing?.map((val) => {
            const data = subscription?.pricing?.filter((item) => item?.id === val?.id) || [];
            return {
              id: val?.id,
              name: val?.invoice_item_type?.name || val?.description,
              price: (val?.unit_price || 0) / 100,
              units: val?.invoice_item_type?.units?.per_unit,
              quantity: data?.length || 0,
            };
          }) || [],
      });
    }
  }, [subscriptionPricing]); // eslint-disable-line

  const decrease = (index, value) => {
    if (value?.quantity > 0) {
      const qty = value?.quantity - 1;
      formRef.current.setFieldValue(`items.${index}.quantity`, value?.quantity - 1);
      if (qty === 0) {
        const data = pricing?.filter((item) => item?.id !== value?.id);
        setPricing(data);
      }
    }
  };

  const increase = (index, value) => {
    if (value?.quantity < 1) {
      const qty = value?.quantity + 1;
      formRef.current.setFieldValue(`items.${index}.quantity`, qty);
      if (qty === 1) {
        const pricingSelected = subscriptionPricing?.find((item) => item?.id === value?.id);
        const data = [...pricing, ...[pricingSelected]];
        setPricing(data);
      }
    }
  };

  return (
    <BaseModal
      size="lg"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      disabled={subscriptionPricing?.length ? false : true}
      loading={fetchingUpdateSubscription}
      title={i18n.t('subscriptions.select_optional_items')}
      enterButtonLabel={i18n.t('common.save')}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={(values) => {
          const data = values?.items.filter((item) => item?.quantity > 0);
          const items = data?.map((val) => {
            return val.id;
          });
          dispatch(
            SubscriptionsActions.fetchUpdateSubscription(subscription?.id, {
              optional_pricing_rules: items,
            })
          );
        }}
        validationSchema={formSchema}
      >
        {({ values, handleSubmit, errors }) => (
          <Form noValidate onSubmit={handleSubmit} autoComplete="off">
            <Grid data-cy="optional-items" container spacing={4}>
              <Grid item xs={12} sm={7} md={6}>
                <FieldArray
                  name="items"
                  render={() => (
                    <div className={classes?.container}>
                      <h4 className={classes?.title}>{i18n.t('subscriptions.items')}</h4>
                      {fetchingSubscriptionPricing ? (
                        <Box justifyContent="center" alignItems="center" display="flex">
                          <Loading />
                        </Box>
                      ) : subscriptionPricing?.length ? (
                        values?.items.map((value, index) => (
                          <div key={index} className={classes?.items}>
                            <p className={classes?.label}>{value?.name}</p>
                            <div>
                              <div className={classes?.numberInput}>
                                <p className={classes?.price}>{`$${value?.price}${value.units}`}</p>
                                <Button
                                  type="button"
                                  disableFocusRipple={true}
                                  onClick={() => {
                                    decrease(index, value);
                                  }}
                                  className={classes.btnMinus}
                                >
                                  -
                                </Button>
                                <Field hidden name={`items.${index}.id`} />
                                <Field
                                  disabled
                                  className={classes.input}
                                  name={`items.${index}.quantity`}
                                />
                                <Button
                                  type="button"
                                  disableFocusRipple={true}
                                  onClick={() => {
                                    increase(index, value);
                                  }}
                                  className={classes.btnPlus}
                                >
                                  +
                                </Button>
                              </div>
                              <span>
                                {errors?.items ? errors?.items[index]?.quantity || '' : ''}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        i18n.t('common.empty_list')
                      )}
                    </div>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={5} md={6}>
                <h4
                  style={{
                    marginBottom: 15,
                  }}
                >
                  {i18n.t('subscriptions.extra_items_total')}
                </h4>
                <OptionalItemsTable pricing={pricing || []} />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

OptionalItems.propTypes = {
  open: PropTypes.bool.isRequired,
  subscription: PropTypes.object.isRequired,
  handleClick: PropTypes.func.isRequired,
};

OptionalItems.defaultProps = {
  open: false,
  subscription: {},
  handleClick: () => {},
};

export default OptionalItems;
